import { useState, useMemo } from "react";
import { Flex, Typography, Segmented, Modal } from "antd";
import { Select } from "@libertify/ui";
import {
  LaptopOutlined,
  MobileOutlined,
  TabletOutlined,
} from "@ant-design/icons";
import { useTranslate } from "hooks/useTranslate/";
import { useAntTheme } from "hooks/useAntTheme";
import { useThemeData } from "../hooks/useThemeData";
import { useCloneTheme, useUpdateTheme } from "../hooks/useThemeApi";
import type { ThemeResponse } from "../hooks/useThemeApi";
import { aspectRatioNumberToEnum } from "../helpers";
import { SlideTemplatePreview } from "../../storyboardEditor/components/Template/SlideTemplatePreview";
import placeholders from "../placeholders";
import { mapSlideToTemplateState } from "../../storyboardEditor/helpers/mapTemplateSlidesToTemplateState";
import { getThumbnailSize } from "../../storyboardEditor/components/StoryboardSlidesThumbnails";
import {
  DESKTOP_ASPECT_RATIO,
  MOBILE_ASPECT_RATIO,
  TABLET_ASPECT_RATIO,
} from "pages/media/constants/";

const noop = () => undefined;

type CreateThemeModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (theme: ThemeResponse) => void;
  publicThemes: ThemeResponse[];
  privateThemes: ThemeResponse[];
};

export const CreateThemeModal = ({
  open,
  onClose,
  onCreated,
  publicThemes,
  privateThemes,
}: CreateThemeModalProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { organizationId, logo, activePresenter, presenterRatio, logoRatio } =
    useThemeData();

  const [selectedBaseThemeId, setSelectedBaseThemeId] = useState<
    string | undefined
  >(undefined);
  const [aspectRatio, setAspectRatio] = useState<number>(MOBILE_ASPECT_RATIO);

  const aspectRatioOptions = [
    {
      label: t(
        "components.templates.templatePreview.mediaResolutionSegmentedButtons.desktop"
      ),
      value: DESKTOP_ASPECT_RATIO,
      icon: <LaptopOutlined />,
    },
    {
      label: t(
        "components.templates.templatePreview.mediaResolutionSegmentedButtons.tablet"
      ),
      value: TABLET_ASPECT_RATIO,
      icon: <TabletOutlined />,
    },
    {
      label: t(
        "components.templates.templatePreview.mediaResolutionSegmentedButtons.mobile"
      ),
      value: MOBILE_ASPECT_RATIO,
      icon: <MobileOutlined />,
    },
  ];

  const cloneTheme = useCloneTheme(organizationId);
  const updateTheme = useUpdateTheme(organizationId);

  const allThemes = useMemo(
    () => [...publicThemes, ...privateThemes],
    [publicThemes, privateThemes]
  );

  const selectedBaseTheme = useMemo(
    () => allThemes.find((theme) => theme.id === selectedBaseThemeId),
    [allThemes, selectedBaseThemeId]
  );

  const previewTemplates = useMemo(() => {
    const commonSlideProps = {
      id: "",
      avatarShot: null,
      music: null,
      inputs: {},
    };
    const commonAssets = {
      brandKitData: { logo, audio: undefined },
      initialPresenter: activePresenter,
      presenterRatio,
      logoRatio,
    };

    const layouts = selectedBaseTheme?.layouts ?? {};

    return Object.entries(placeholders.DEFAULT_SLIDES_ITEMS)
      .map(([key, defaultValue]) => {
        const template = mapSlideToTemplateState(
          {
            ...commonSlideProps,
            sprite_definitions: layouts[key]?.layout ?? defaultValue,
            name: key,
          },
          placeholders.DEFAULT_SLIDES_PLACEHOLDERS,
          aspectRatio,
          commonAssets
        );

        const slide = placeholders.DEFAULT_SLIDES_PLACEHOLDERS.find(
          (s) => s.slide_type === key
        );

        return { key, template, slide };
      })
      .filter(
        (
          item
        ): item is typeof item & { slide: NonNullable<typeof item.slide> } =>
          item.slide !== undefined
      );
  }, [
    selectedBaseTheme,
    logo,
    activePresenter,
    presenterRatio,
    logoRatio,
    aspectRatio,
  ]);

  const handleSubmit = async () => {
    if (!selectedBaseThemeId) return;

    const clonedTheme = await cloneTheme.mutateAsync(selectedBaseThemeId);
    const nextAspectRatio = aspectRatioNumberToEnum(aspectRatio);
    const newTheme =
      clonedTheme.aspect_ratio === nextAspectRatio
        ? clonedTheme
        : await updateTheme.mutateAsync({
            themeId: clonedTheme.id,
            data: { aspect_ratio: nextAspectRatio },
          });

    setSelectedBaseThemeId(undefined);
    setAspectRatio(MOBILE_ASPECT_RATIO);
    onClose();
    onCreated(newTheme);
  };

  const handleCancel = () => {
    setSelectedBaseThemeId(undefined);
    setAspectRatio(MOBILE_ASPECT_RATIO);
    onClose();
  };

  return (
    <Modal
      title={t("media.themeEditor.createModal.title")}
      open={open}
      onOk={() => void handleSubmit()}
      onCancel={handleCancel}
      okText={t("media.themeEditor.createModal.create")}
      cancelText={t("buttons.cancel")}
      okButtonProps={{ disabled: !selectedBaseThemeId }}
      confirmLoading={cloneTheme.isPending || updateTheme.isPending}
      width={700}
    >
      <Flex vertical gap={16}>
        <Flex vertical gap={8}>
          <Typography.Text strong>
            {t("components.templates.settings.layoutTab.options")}
          </Typography.Text>
          <Segmented
            options={aspectRatioOptions.map((option) => ({
              label: (
                <Flex
                  vertical
                  align="center"
                  justify="center"
                  gap={2}
                  style={{ padding: theme.paddingXS }}
                >
                  {option.icon}
                  <Typography.Text>{option.label}</Typography.Text>
                </Flex>
              ),
              value: option.value,
            }))}
            value={aspectRatio}
            onChange={(value) => setAspectRatio(value)}
            style={{
              backgroundColor: theme.colorBgContainerDisabled,
              padding: theme.paddingXS,
              width: "fit-content",
            }}
          />
        </Flex>
        <Select
          allowClear
          placeholder={t("media.themeEditor.createModal.selectPublicTheme")}
          value={selectedBaseThemeId}
          onChange={setSelectedBaseThemeId}
          options={[
            ...(publicThemes.length > 0
              ? [
                  {
                    label: t("media.themeEditor.publicThemes"),
                    options: publicThemes.map((themeItem) => ({
                      label: themeItem.name,
                      value: themeItem.id,
                    })),
                  },
                ]
              : []),
            ...(privateThemes.length > 0
              ? [
                  {
                    label: t("media.themeEditor.myThemes"),
                    options: privateThemes.map((themeItem) => ({
                      label: themeItem.name,
                      value: themeItem.id,
                    })),
                  },
                ]
              : []),
          ]}
          style={{ width: "100%" }}
        />

        {selectedBaseThemeId && previewTemplates.length > 0 && (
          <Flex vertical gap={8}>
            <Typography.Text strong>
              {t("media.themeEditor.createModal.preview")}
            </Typography.Text>
            <Flex
              gap={12}
              style={{
                overflowX: "auto",
                paddingBlockEnd: 8,
              }}
            >
              {previewTemplates.map(({ key, template, slide }) => (
                <Flex
                  key={key}
                  vertical
                  align="center"
                  gap={4}
                  style={{ flexShrink: 0 }}
                >
                  <SlideTemplatePreview
                    opacity={1}
                    templateSize={getThumbnailSize(aspectRatio)}
                    border={{}}
                    onCardClick={noop}
                    slide={slide}
                    template={template}
                  />
                  <Typography.Text
                    style={{ fontSize: 11, textTransform: "uppercase" }}
                  >
                    {key.replace(/_/g, " ")}
                  </Typography.Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Modal>
  );
};
